import { storage } from '@/lib/storage';
import { GRID_PER_PAGE_DEFAULT } from '@/domain/documents/constants';
import type { PaymentInfoRecord, PaymentMetaOptionDetails } from '@/domain/payments/types';
import type { PaymentBeneficiary } from '@/domain/payments/schemas';

/**
 * Named predicate rather than a truthiness test at each call site: `payTo` used to
 * be a string id, and now that it is an object `Boolean(payTo)` would report every
 * payment as having a beneficiary, including one that has none.
 */
export const hasPaymentBeneficiary = (paymentInfo: PaymentInfoRecord): boolean => paymentInfo.payTo != null;

const camelToSnake = (key: string): string => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/**
 * The payment's beneficiary details, re-keyed for the beneficiary form.
 *
 * The two contracts differ only in naming style (`accountNumber` vs
 * `account_number`), so this converts by rule rather than by a table of field
 * names — a field the backend adds later needs no edit here.
 */
export const paymentBeneficiaryToFormValues = (beneficiary: PaymentBeneficiary): PaymentMetaOptionDetails =>
  Object.fromEntries(
    Object.entries(beneficiary)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      .map(([key, value]) => [camelToSnake(key), value]),
  );

export const getPaymentsGridPerPage = () => {
  const stored = storage().paymentsAgGridPerPage().get();

  return stored ? JSON.parse(stored) : GRID_PER_PAGE_DEFAULT;
};

export const setPaymentsGridPerPage = (value: number): void => {
  storage().paymentsAgGridPerPage().set(JSON.stringify(value));
};
